/**
 * BlockedDomainsManager Component
 *
 * Super Admin component for managing blocked email domains.
 * Allows viewing, adding, and removing domains that are blocked from registration.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Delete,
  Search,
  Refresh,
  CloudDownload,
  Block,
} from '@mui/icons-material';
import {
  adminBlockedDomainsApi,
  BlockedEmailDomain,
} from '../../services/admin';
import { getErrorMessage } from '../../services/api';
import { ConfirmDialog } from '../common/ConfirmDialog';

export default function BlockedDomainsManager() {
  const [domains, setDomains] = useState<BlockedEmailDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Add domain dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newReason, setNewReason] = useState('');

  // Unblock confirmation dialog state
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [domainToUnblock, setDomainToUnblock] = useState<BlockedEmailDomain | null>(null);

  // Fetch domains
  const fetchDomains = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminBlockedDomainsApi.list();
      setDomains(result.domains);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  // Add domain
  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      setError('Domain is required');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      const result = await adminBlockedDomainsApi.add({
        domain: newDomain.trim(),
        reason: newReason.trim() || undefined,
      });
      setSuccess(result.message);
      setAddDialogOpen(false);
      setNewDomain('');
      setNewReason('');
      await fetchDomains();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Open unblock confirmation dialog
  const handleOpenUnblockDialog = (domain: BlockedEmailDomain) => {
    setDomainToUnblock(domain);
    setUnblockDialogOpen(true);
  };

  // Close unblock confirmation dialog
  const handleCloseUnblockDialog = () => {
    setUnblockDialogOpen(false);
    setDomainToUnblock(null);
  };

  // Confirm unblock domain
  const handleConfirmUnblock = async () => {
    if (!domainToUnblock) return;

    try {
      setActionLoading(true);
      setError(null);
      const result = await adminBlockedDomainsApi.remove(domainToUnblock._id);
      setSuccess(result.message);
      setUnblockDialogOpen(false);
      setDomainToUnblock(null);
      await fetchDomains();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Seed built-in domains
  const handleSeedDomains = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const result = await adminBlockedDomainsApi.seed();
      setSuccess(result.message);
      await fetchDomains();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Filter domains by search query
  const filteredDomains = domains.filter(
    (d) =>
      d.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.reason?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format date for display
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  // Get display name for user
  const getAddedByName = (domain: BlockedEmailDomain) => {
    if (domain.addedBy?.profile?.firstName) {
      return `${domain.addedBy.profile.firstName} ${domain.addedBy.profile.lastName || ''}`.trim();
    }
    return domain.addedBy?.email || 'System';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Block color="error" />
            <Typography variant="h6">Blocked Email Domains</Typography>
            <Chip label={domains.length} size="small" color="default" />
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Import default disposable email domains">
              <Button
                size="small"
                startIcon={<CloudDownload />}
                onClick={handleSeedDomains}
                disabled={actionLoading}
              >
                Seed Defaults
              </Button>
            </Tooltip>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={fetchDomains}
              disabled={actionLoading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              disabled={actionLoading}
            >
              Add Domain
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search domains..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Table */}
        {filteredDomains.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">
              {searchQuery
                ? 'No domains match your search'
                : 'No blocked domains yet. Click "Seed Defaults" to import common disposable email domains.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Added By</TableCell>
                  <TableCell>Date Added</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDomains.map((domain) => (
                  <TableRow key={domain._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {domain.domain}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {domain.reason || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getAddedByName(domain)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(domain.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={domain.isBuiltIn ? 'Built-in' : 'Custom'}
                        size="small"
                        color={domain.isBuiltIn ? 'default' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from blocklist">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenUnblockDialog(domain)}
                          disabled={actionLoading}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Domain Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Blocked Domain</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Users will not be able to register with email addresses from this domain.
              </Typography>
              <TextField
                label="Domain"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                fullWidth
                placeholder="example.com"
                helperText="Enter the domain without @ symbol"
                autoFocus
              />
              <TextField
                label="Reason (optional)"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                fullWidth
                placeholder="e.g., Disposable email service"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddDomain}
              disabled={actionLoading || !newDomain.trim()}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <Add />}
            >
              Add Domain
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unblock Confirmation Dialog */}
        <ConfirmDialog
          open={unblockDialogOpen}
          title="Unblock Domain"
          message={`Are you sure you want to remove "${domainToUnblock?.domain}" from the blocklist? Users will be able to register with this email domain again.`}
          confirmText="Unblock"
          cancelText="Cancel"
          variant="warning"
          onConfirm={handleConfirmUnblock}
          onCancel={handleCloseUnblockDialog}
          isLoading={actionLoading}
        />
      </CardContent>
    </Card>
  );
}
